import pageData from './pageData'

const STORAGE_KEY = 'ml_lang'
const SWITCH_KEY = 'ml_lang_switch'
const SECTION_HASHES = { about: 'about', books: 'works', press: 'press', contact: 'contact' }

const CLOSE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" /></svg>'

const storage = (type) => {
    try {
        return window[type]
    } catch (error) {
        return null
    }
}

export const consumeLangSwitch = () => {
    const session = storage('sessionStorage')
    if (!session || !session.getItem(SWITCH_KEY)) return false
    session.removeItem(SWITCH_KEY)
    return true
}

export const sectionFromHash = () => {
    const hash = location.hash.slice(1)
    return Object.keys(SECTION_HASHES).find((name) => SECTION_HASHES[name] === hash) || null
}

export default class LangSwitch {

    constructor(experience) {
        this.experience = experience
        this.data = pageData()
        this.lang = this.data.lang
        this.switches = document.querySelectorAll('.lang-switch')

        this.addLinkListeners()
        this.addHeaderFocus()
        this.syncMenuFocus()
        this.setupSuggestion()
    }

    addLinkListeners() {
        this.switches.forEach((nav) => {
            nav.querySelectorAll('.lang-switch-link').forEach((link) => {
                link.addEventListener('click', (event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return
                    event.preventDefault()
                    if (link.getAttribute('aria-current') === 'page') return
                    nav.classList.add(`lang-switch-leaving-${this.lang}`)
                    this.go(link.dataset.lang, link.getAttribute('href'))
                })
            })
        })
    }

    go(lang, href) {
        const local = storage('localStorage')
        const session = storage('sessionStorage')
        if (local) local.setItem(STORAGE_KEY, lang)
        if (session) session.setItem(SWITCH_KEY, '1')

        const section = this.currentSection()
        const hash = section ? `#${SECTION_HASHES[section]}` : ''
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        setTimeout(() => location.assign(href + hash), reduce ? 0 : 220)
    }

    currentSection() {
        const ui = this.experience.ui
        if (!ui || !ui.sections || !ui.scroll || !ui.landingPage || ui.landingPage.visible) return null

        let current = null
        ui.sections.sections.forEach((section) => {
            if (SECTION_HASHES[section.name] && ui.scroll.scrollY + (window.innerHeight / 2) >= section.y)
                current = section.name
        })
        return current
    }

    addHeaderFocus() {
        const header = document.getElementById('header-container')
        if (!header) return
        header.addEventListener('focusin', () => {
            const ui = this.experience.ui
            if (ui && ui.header) ui.header.show()
        })
    }

    syncMenuFocus() {
        const links = document.querySelectorAll('#menu-lang-switch .lang-switch-link')
        if (!links.length) return

        const bind = () => {
            const menu = this.experience.ui && this.experience.ui.menu && this.experience.ui.menu.main
            if (!menu) return false
            this.menu = menu
            menu.on('open', () => {
                links.forEach((link) => link.removeAttribute('tabindex'))
                this.toggleSuggestionAway(true)
            })
            menu.on('hide', () => {
                links.forEach((link) => link.setAttribute('tabindex', '-1'))
                this.toggleSuggestionAway(false)
            })
            return true
        }

        if (!bind()) this.experience.resources.on('ready', () => setTimeout(bind))
    }

    preferredLang() {
        const languages = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || '']
        return String(languages[0] || '').toLowerCase().split('-')[0]
    }

    setupSuggestion() {
        const alternate = this.data.alternate
        const local = storage('localStorage')
        if (!alternate || !alternate.suggest || !local || local.getItem(STORAGE_KEY)) return

        const preferred = this.preferredLang()
        if (!preferred || preferred === this.lang) return
        if (preferred !== alternate.lang && alternate.lang !== 'en') return

        const start = () => setTimeout(() => this.whenConsentClear(() => this.showSuggestion()), 1600)
        if (document.getElementById('intro-container')?.classList.contains('hide')) start()
        else window.addEventListener('intro-finished', start, { once: true })
    }

    consentOpen() {
        const consent = document.getElementById('consent')
        return Boolean(consent && !consent.hidden)
    }

    whenConsentClear(callback) {
        if (!this.consentOpen()) return callback()

        const consent = document.getElementById('consent')
        const observer = new MutationObserver(() => {
            if (this.consentOpen()) return
            observer.disconnect()
            setTimeout(() => this.whenConsentClear(callback), 1200)
        })
        observer.observe(consent, { attributes: true, attributeFilter: ['hidden'] })
    }

    showSuggestion() {
        const { suggest, href, lang } = this.data.alternate
        const root = document.createElement('aside')
        root.id = 'lang-suggest'
        root.className = 'lang-suggest'
        root.lang = lang
        root.hidden = true
        root.setAttribute('aria-labelledby', 'lang-suggest-title')
        root.innerHTML = `
            <p id="lang-suggest-title" class="lang-suggest-title"></p>
            <p class="lang-suggest-text"></p>
            <div class="lang-suggest-actions">
                <a class="lang-suggest-accept" hreflang="${lang}"></a>
                <button type="button" class="lang-suggest-dismiss"></button>
            </div>
            <button type="button" class="lang-suggest-close">${CLOSE_ICON}</button>`

        root.querySelector('.lang-suggest-title').textContent = suggest.title
        root.querySelector('.lang-suggest-text').textContent = suggest.text
        const accept = root.querySelector('.lang-suggest-accept')
        accept.textContent = suggest.accept
        accept.setAttribute('href', href)
        root.querySelector('.lang-suggest-dismiss').textContent = suggest.dismiss
        root.querySelector('.lang-suggest-close').setAttribute('aria-label', suggest.dismiss)

        accept.addEventListener('click', (event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return
            event.preventDefault()
            this.go(lang, href)
        })

        const dismiss = () => {
            const local = storage('localStorage')
            if (local) local.setItem(STORAGE_KEY, this.lang)
            this.hideSuggestion(root)
        }
        root.querySelector('.lang-suggest-dismiss').addEventListener('click', dismiss)
        root.querySelector('.lang-suggest-close').addEventListener('click', dismiss)
        root.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') dismiss()
        })
        const consent = document.getElementById('consent')
        if (consent) {
            this.consentObserver = new MutationObserver(() => {
                if (this.consentOpen()) this.hideSuggestion(root)
            })
            this.consentObserver.observe(consent, { attributes: true, attributeFilter: ['hidden'] })
        }

        document.body.appendChild(root)
        this.suggestion = root
        this.toggleSuggestionAway(Boolean(this.menu && this.menu.visible))
        root.hidden = false
        requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('lang-suggest-visible')))
    }

    toggleSuggestionAway(away) {
        if (!this.suggestion) return
        this.suggestion.classList.toggle('lang-suggest-away', away)
        this.suggestion.inert = away
    }

    hideSuggestion(root) {
        this.suggestion = null
        if (this.consentObserver) this.consentObserver.disconnect()
        root.classList.remove('lang-suggest-visible')
        setTimeout(() => root.remove(), 450)
    }
}
